import styled, { css, keyframes } from 'styled-components';
import { defaultTheme } from '@styles/theme';
import * as Accordion from '@radix-ui/react-accordion';

const openCardAnimation = keyframes`
  from {
    padding: ${defaultTheme.elsie.dimension[12]};
    background-color: ${defaultTheme.elsie.colors.inverse.surface.accent.default};
    width: 200px;
  }
  to {
    padding: ${defaultTheme.elsie.dimension[12]}  ${defaultTheme.elsie.dimension[20]};
    background-color: ${defaultTheme.elsie.colors.inverse.canvas.subtle};
    width: 400px;
  }
`;

const closeCardAnimation = keyframes`
  from {
    padding: ${defaultTheme.elsie.dimension[12]}  ${defaultTheme.elsie.dimension[20]};
    background-color: ${defaultTheme.elsie.colors.inverse.canvas.subtle};
    width: 400px;
  }
  to {
    padding: ${defaultTheme.elsie.dimension[12]};
    background-color: ${defaultTheme.elsie.colors.inverse.surface.accent.default};
    width: 200px;
  }`;

export const TimelineContainer = styled.section`
  background-color: ${({ theme }) => theme.elsie.colors.inverse.canvas.faint};
  padding: 80px 0;
`;

export const TimelineTitle = styled.h2`
  color: ${({ theme }) => theme.elsie.colors.inverse.content.accent};
  font-size: 36px;
  font-weight: ${({ theme }) => theme.elsie.fontWeight.bold};
  line-height: ${({ theme }) => theme.elsie.lineHeight.md};
  letter-spacing: -0.32px;
  padding: 0 64px;
  margin: 0 0 64px;
`;

export const Timeline = styled.div`
  height: 441px;
  position: relative;
`;

export const TimelineLine = styled.div`
  width: 100%;
  height: ${({ theme }) => theme.elsie.dimension[12]};
  background-color: ${({ theme }) => theme.elsie.colors.highlight.blueBerry.faint};
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
`;

export const TimelineStepsContainer = styled(Accordion.Root)`
  padding: 12px 64px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 64px;
  right: 64px;
`;

export const TimelineStep = styled.div<{ isOpen: boolean }>`
  width: fit-content;
  position: absolute;
  z-index: ${({ isOpen }) => (isOpen ? 15 : 0)};
  ${({ isOpen }) =>
    !isOpen &&
    css`
      transition: z-index 0.3s step-end;
    `};

  &[data-position='0'] {
    top: 0;
    left: 0;
  }
  &[data-position='1'] {
    bottom: 0;
    left: 20%;
    transform: translateX(-20%);
    display: flex;
    flex-direction: column-reverse;
  }
  &[data-position='2'] {
    top: 0;
    left: 40%;
    transform: translateX(-40%);
  }
  &[data-position='3'] {
    bottom: 0;
    left: 60%;
    transform: translateX(-60%);
    display: flex;
    flex-direction: column-reverse;
  }
  &[data-position='4'] {
    top: 0;
    left: 80%;
    transform: translateX(-80%);
  }
  &[data-position='5'] {
    bottom: 0;
    left: 100%;
    transform: translateX(-100%);
    display: flex;
    flex-direction: column-reverse;
  }
`;

export const TimelineConnector = styled.div<{ isOpen: boolean }>`
  width: 2px;
  height: 130px;
  margin: 0 auto;
  background: repeating-linear-gradient(to bottom, #ffffff1c, #ffffff1c 4px, transparent 4px, transparent 12px);
  display: ${({ isOpen }) => (isOpen ? 'none' : 'block')};
`;

export const TimelineCard = styled(Accordion.Item)`
  background-color: ${({ theme }) => theme.elsie.colors.inverse.surface.accent.default};
  border: 1px solid ${({ theme }) => theme.elsie.colors.inverse.border.subtle};
  border-radius: ${({ theme }) => theme.elsie.borderRadius.md};
  padding: ${({ theme }) => theme.elsie.dimension[12]};
  width: 200px;
  cursor: pointer;
  position: relative;
  &[data-state='open'] {
    animation: ${openCardAnimation} 0.3s forwards;
    z-index: 15;
  }
  &[data-state='closed'] {
    animation: ${closeCardAnimation} 0.3s forwards;
    z-index: 0;
    transition: z-index 0.3s step-end;
  }
`;

export const TimelineCardInteractive = styled.div`
  background-color: ${({ theme }) => theme.elsie.colors.inverse.interactive.soft.default};
  color: ${({ theme }) => theme.elsie.colors.inverse.content.onInteractive.soft};
  border: none;
  border-radius: ${({ theme }) => theme.elsie.borderRadius.md};
  width: ${({ theme }) => theme.elsie.dimension[24]};
  height: ${({ theme }) => theme.elsie.dimension[24]};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${({ theme }) => theme.elsie.dimension[4]};
  position: relative;
  left: calc(100% - 8px);
  transform: translateX(-100%);
`;

export const TimelineCardTitle = styled(Accordion.Header)`
  color: ${({ theme }) => theme.elsie.colors.content.onInteractive.accent};
  margin: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.elsie.dimension[8]};
  font-size: 20px;
  font-weight: ${({ theme }) => theme.elsie.fontWeight.bold};
  line-height: ${({ theme }) => theme.elsie.lineHeight.lg};
  letter-spacing: -1px;
`;

export const TimelineCardIcon = styled.span`
  background-color: ${({ theme }) => theme.elsie.colors.interactive.subtle.hovered};
  color: ${({ theme }) => theme.elsie.colors.content.onInteractive.subtle};
  border-radius: ${({ theme }) => theme.elsie.borderRadius.sm};
  padding: ${({ theme }) => theme.elsie.dimension[4]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const TimelineCardText = styled(Accordion.Content)`
  color: ${({ theme }) => theme.elsie.colors.content.onInteractive.accent};
  margin-top: ${({ theme }) => theme.elsie.dimension[8]};
  font-size: 15px;
  font-weight: ${({ theme }) => theme.elsie.fontWeight.regular};
  line-height: ${({ theme }) => theme.elsie.lineHeight.lg};
  letter-spacing: ${({ theme }) => theme.elsie.letterSpacing.sm};
`;

export const TimelineCardSubtext = styled.div`
  color: ${({ theme }) => theme.elsie.colors.inverse.content.soft};
  margin: ${({ theme }) => theme.elsie.dimension[8]} 0;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.elsie.fontWeight.semiBold};
  line-height: ${({ theme }) => theme.elsie.lineHeight.lg};
  letter-spacing: ${({ theme }) => theme.elsie.letterSpacing.sm};
`;

export const TimelineCardHighlightContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.elsie.dimension[8]};
`;

export const TimelineCardHighlight = styled.span`
  color: ${({ theme }) => theme.elsie.colors.highlight.blueBerry.accent};
  background-color: ${({ theme }) => theme.elsie.colors.highlight.blueBerry.faint};
  padding: ${({ theme }) => theme.elsie.dimension[2]} ${({ theme }) => theme.elsie.dimension[6]};
  border-radius: ${({ theme }) => theme.elsie.borderRadius.sm};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.elsie.fontWeight.semiBold};
  line-height: ${({ theme }) => theme.elsie.lineHeight.lg};
  letter-spacing: ${({ theme }) => theme.elsie.letterSpacing.sm};
  white-space: nowrap;
`;

export const TimelineBlur = styled.div<{ show: boolean }>`
  background-color: #2f2f2f33;
  backdrop-filter: blur(8px);
  opacity: 0;
  z-index: -1;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: opacity 0.3s ease-in-out, z-index 0.3s step-end;
  ${({ show }) =>
    show &&
    css`
      opacity: 1;
      z-index: 10;
      transition: opacity 0.3s ease-in-out, z-index 0.3s step-start;
    `}
`;
